import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignServiceEventDispatch,
  listServiceEvents,
  listServiceRigs,
  listServiceTechs,
  listServiceWorkQueues,
  updateServiceEvent,
} from "@/lib/service/client";
import type { ServiceEvent, ServiceRig, ServiceTech, ServiceWorkQueue } from "@/lib/service/types";

export function useServiceBoardPanel(navTick: number) {
  const [queues, setQueues] = useState<ServiceWorkQueue[]>([]);
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [techs, setTechs] = useState<ServiceTech[]>([]);
  const [rigs, setRigs] = useState<ServiceRig[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignWarnings, setAssignWarnings] = useState<string[]>([]);
  const [assignBusyRigId, setAssignBusyRigId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [queueFilter, setQueueFilter] = useState<string>("all");
  const [routeComposerTick, setRouteComposerTick] = useState(0);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const [queuesRes, eventsRes, techsRes, rigsRes] = await Promise.all([
      listServiceWorkQueues({ active_only: true }),
      listServiceEvents(
        queueFilter === "all" ? undefined : { queue_id: queueFilter }
      ),
      listServiceTechs({ active_only: true }),
      listServiceRigs({ active_only: true }),
    ]);
    setListBusy(false);
    if (!queuesRes.ok) {
      setLocalError(queuesRes.error);
      return;
    }
    if (!eventsRes.ok) {
      setLocalError(eventsRes.error);
      return;
    }
    if (!techsRes.ok) {
      setLocalError(techsRes.error);
      return;
    }
    if (!rigsRes.ok) {
      setLocalError(rigsRes.error);
      return;
    }
    setQueues(queuesRes.data.queues);
    setEvents(eventsRes.data.events);
    setTechs(techsRes.data.techs);
    setRigs(rigsRes.data.rigs);
    setRouteComposerTick((s) => s + 1);
  }, [queueFilter]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const eventsByQueue = useMemo(() => {
    const map = new Map<string, ServiceEvent[]>();
    for (const event of events) {
      const key = event.queue_id ?? "unassigned";
      const list = map.get(key) ?? [];
      list.push(event);
      map.set(key, list);
    }
    return map;
  }, [events]);

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId) ?? null,
    [events, selectedEventId]
  );

  const clearAssignFeedback = useCallback(() => {
    setAssignError(null);
    setAssignWarnings([]);
  }, []);

  const scheduleEvent = useCallback(
    async (payload: {
      event_id: string;
      scheduled_date: string;
      scheduled_window_label?: string;
      tech_id?: string | null;
      assigned_rig_id?: string | null;
    }) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await updateServiceEvent(payload);
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return false;
      }
      await reload();
      return true;
    },
    [reload]
  );

  const assignDispatch = useCallback(
    async (eventId: string, rigId: string, techId?: string | null) => {
      setAssignBusyRigId(rigId);
      setAssignError(null);
      setAssignWarnings([]);
      const event = events.find((e) => e.id === eventId);
      const res = await assignServiceEventDispatch({
        event_id: eventId,
        rig_id: rigId,
        tech_id: techId,
        scheduled_date: event?.scheduled_date ?? undefined,
      });
      setAssignBusyRigId(null);
      if (!res.ok) {
        setAssignError(res.error);
        return false;
      }
      setAssignWarnings(res.data.warnings ?? []);
      await reload();
      return true;
    },
    [events, reload]
  );

  const bumpRouteComposer = useCallback(() => {
    setRouteComposerTick((s) => s + 1);
  }, []);

  return {
    queues,
    events,
    eventsByQueue,
    techs,
    rigs,
    listBusy,
    actionBusy,
    localError,
    assignError,
    assignWarnings,
    assignBusyRigId,
    clearAssignFeedback,
    queueFilter,
    setQueueFilter,
    selectedEventId,
    setSelectedEventId,
    selectedEvent,
    scheduleEvent,
    assignDispatch,
    reload,
    routeComposerTick,
    bumpRouteComposer,
  };
}
